"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, LogOut } from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import {
  isRecoverableClientBoundaryError,
  recordDeploymentReloadAttempt,
  shouldAttemptDeploymentReload,
} from "@/lib/observability/client-error-boundary";
import { reportClientError } from "@/lib/observability/report-client-error";
import { humanizeProcessingError } from "@/lib/utils/humanize-processing-error";
import { trackEvent } from "@/lib/analytics/amplitude";
import {
  ERROR_PAGE_SHOWN,
  ERROR_RETRY_CLICKED,
} from "@/lib/analytics/amplitude-events";

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { t } = useJsonTranslation("AppError");

  useEffect(() => {
    const isDeploymentMismatch = isRecoverableClientBoundaryError(error);
    const pathname = window.location.pathname;

    if (isDeploymentMismatch && shouldAttemptDeploymentReload(pathname)) {
      recordDeploymentReloadAttempt(pathname);
      window.location.reload();
      return;
    }

    // Deployment mismatch errors are expected during rolling updates and
    // auto-recover via reload. Only log a quiet warning.
    if (isDeploymentMismatch) {
      console.warn(
        "[error-boundary] Deployment mismatch persisted after reload:",
        error.message
      );
      return;
    }

    reportClientError(error, {
      source: "app.error.boundary",
      severity: "error",
      extra: {
        digest: error.digest,
        page: window.location.pathname,
        userAgent: navigator.userAgent,
      },
    });

    // Backup: send error to server log so it appears in kubectl logs
    fetch("/api/observability/client-error/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 2000),
        digest: error.digest,
        page: window.location.pathname,
        userAgent: navigator.userAgent,
      }),
      credentials: "include",
    }).catch((err) => {
      console.error("[error-boundary] Failed to report error to server:", err);
    });

    trackEvent(ERROR_PAGE_SHOWN, {
      errorMessage: error.message,
      digest: error.digest,
      page: window.location.pathname,
      boundary_surface: "app",
    });
  }, [error]);

  // SENTRY FIX: NODE-20X/NODE-224/NODE-2FF — Actionable error messages
  // Date: 2026-04-14 | Agent: Claude Code
  // Root cause: Generic "Something went wrong" for all errors — no guidance.
  // Fix: humanizeProcessingError translates FS/network/timeout errors into
  //   specific actionable messages for the user.
  const humanizedMessage = humanizeProcessingError(error, t("description"));
  const isSpecificError = humanizedMessage !== t("description");

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8 text-center">
      <div className="mx-auto max-w-md space-y-6">
        <div className="bg-destructive/10 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
          <AlertTriangle className="text-destructive h-8 w-8" />
        </div>
        <div className="space-y-2">
          <h2 className="text-foreground text-2xl font-bold">{t("title")}</h2>
          <p className="text-muted-foreground">{humanizedMessage}</p>
          {isSpecificError && (
            <p className="text-muted-foreground/60 text-sm">
              {t("description")}
            </p>
          )}
        </div>
        {process.env.NODE_ENV === "development" && (
          <div className="bg-muted rounded-lg p-4 text-left">
            <p className="text-muted-foreground mb-2 text-xs">
              {t("devLabel")}
            </p>
            <p className="text-destructive font-mono text-xs break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-muted-foreground mt-1 font-mono text-[10px]">
                {error.digest}
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            onClick={() => {
              trackEvent(ERROR_RETRY_CLICKED, {
                page: window.location.pathname,
                boundary_surface: "app",
              });
              reset();
            }}
            variant="default"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("actions.tryAgain")}
          </Button>
          <Button
            onClick={() => (window.location.href = "/login")}
            variant="outline"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t("actions.logOut")}
          </Button>
          <Button onClick={() => (window.location.href = "/")} variant="ghost">
            <Home className="mr-2 h-4 w-4" />
            {t("actions.goHome")}
          </Button>
        </div>
      </div>
    </div>
  );
}
